import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateRestaurantOrderCutoffTimesMutation } from '@durma-soft/gros-sdk/dist/hooks/mutations';
import {
  useAuthUserRestaurant,
  useGetRestaurantOrderCutoffTimesSuspenseQuery,
} from '@durma-soft/gros-sdk';

import { useChangedObject } from '@/hooks/use-changed-object';

import { DataTable } from '@/components/shared/data-table';
import { Button } from '@/components/shared/shadcn-ui/button';

import { columns } from '@/config/restaurant/orders-cutoff';
import { OrderCutoffTimeFormItem } from '@/types/order-cutoff-times';
import { formatOrderCutoffTime, formatTime, repeat } from '@/utils/helpers';

const OrderCutoffTimesTable = () => {
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();

  const updateOrderCutoffTimes = useUpdateRestaurantOrderCutoffTimesMutation();

  const { data } = useGetRestaurantOrderCutoffTimesSuspenseQuery(restaurant.id);

  const originalOrderCutoffTimes = useMemo(() => {
    if (data.length === 7)
      return data
        .map((item) => ({
          day_of_week: item.day_of_week,
          days_in_advance: item.days_in_advance,
          cutoff_time: formatOrderCutoffTime(item.cutoff_time),
        }))
        .sort(
          (a, b) =>
            (a.day_of_week === 0 ? 7 : a.day_of_week) -
            (b.day_of_week === 0 ? 7 : b.day_of_week),
        );

    return repeat(7)
      .map((index) => ({
        day_of_week: index,
        days_in_advance: 1,
        cutoff_time: formatOrderCutoffTime(restaurant.order_cutoff_time),
      }))
      .sort(
        (a, b) =>
          (a.day_of_week === 0 ? 7 : a.day_of_week) -
          (b.day_of_week === 0 ? 7 : b.day_of_week),
      );
  }, [data, restaurant.order_cutoff_time]);

  const [orderCutoffTimes, setOrderCutoffTimes] = useState<
    OrderCutoffTimeFormItem[]
  >(originalOrderCutoffTimes);

  const [hasChanges] = useChangedObject(
    orderCutoffTimes,
    originalOrderCutoffTimes,
  );

  const memoizedColumns = useMemo(() => {
    return columns(t, orderCutoffTimes, setOrderCutoffTimes);
  }, [t, orderCutoffTimes, setOrderCutoffTimes]);

  const handleUpdateOrderCutoffTimes = async () => {
    try {
      await updateOrderCutoffTimes.mutateAsync({
        restaurant_id: restaurant.id,
        order_cutoff_times: orderCutoffTimes.map((item) => ({
          day_of_week: item.day_of_week,
          days_in_advance: item.days_in_advance,
          cutoff_time: formatTime(item.cutoff_time),
        })),
      });
      toast.success(t('order-cutoff-times.success-msg'));
    } catch (error) {
      console.error(error);
      toast.error(t('order-cutoff-times.error-msg'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <DataTable columns={memoizedColumns} data={orderCutoffTimes} />
      <Button
        onClick={handleUpdateOrderCutoffTimes}
        isLoading={updateOrderCutoffTimes.isPending}
        disabled={updateOrderCutoffTimes.isPending || !hasChanges}
      >
        {t('common.save')}
      </Button>
    </div>
  );
};

export default OrderCutoffTimesTable;
